import { FC, SVGProps, useEffect } from "react";
import { Link } from "react-router-dom";

import { Icon } from "atoms/icon/Icon";
import Right from "atoms/icon/icons/chevron_right_small.svg";
import { BadgeTag } from "atoms/tag/BadgeTag";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";
import { TextButton } from "atoms/text-button/TextButton";
import { useIntersectionObserver } from "hooks/useIntersectionObserver";
import { usePrefetchPDP } from "hooks/usePrefetchPDP";
import { formatCurrency } from "toolbox/Money";

type AllOrNone<T> = T | { [K in keyof T]?: undefined };

type BtnProps = AllOrNone<{
  /** Primary button text to display below the carousel */
  primary: string;

  /** Handler that is called when the primary button is clicked */
  onButtonClick: () => void;
}>;

type Card = {
  /** Route that this card will link to */
  to: string;

  /** Source url of the banner image */
  imageSrc: string;

  /** Descriptive tag text that displays below banner image */
  tag: string | null | undefined;

  /** The main text of this card */
  title: string;

  /** Discount text that appears on tag */
  discount?: string | null;

  /** Custom color for the discount tag */
  customColor?: string | null;
};

interface PriceCard extends Card {
  /** The regular price of this card's product */
  price: number;

  /** The member price of this card's product */
  memberPrice?: number | null;
}

interface PromoCard extends Card {
  /** Alert-style tag that shows up on the upper left of the card */
  alert?: string | null;

  /** Navigation text that appears below the title */
  primary: string;
}

type Props = {
  /** SVG icon to render at the very top of the carousel */
  svg?: FC<SVGProps<SVGSVGElement>> | null;

  /** Allcaps text that appears at the top of the carousel */
  title: string;

  /** Descriptive text that appears below the title */
  description?: string | null;

  /** Information to display in the carousel cards */
  cards: (PriceCard | PromoCard)[];

  /** Hide prices */
  guestMode?: boolean;
};

const PriceFooter = (props: PriceCard) => (
  <div className="flex">
    <div className="mr-auto flex items-center gap-2">
      <span className="text-body2 font-semibold">
        {formatCurrency(props.price)}
      </span>
      {!!props.memberPrice && (
        <span className="border-l border-black pl-2 text-body2 font-semibold">
          {formatCurrency(props.memberPrice)} Member
        </span>
      )}
    </div>
    <Icon svg={Right} size="xsmall" color="brandColor" />
  </div>
);

const PromoFooter = (props: PromoCard) => (
  <div className="flex items-center justify-between text-body2 font-semibold text-brand-color">
    {props.primary}
    <Icon svg={Right} size="xsmall" color="brandColor" />
  </div>
);

const GuestFooter = () => (
  <div className="flex items-center justify-between text-body2">
    Sign up to see price
    <Icon svg={Right} size="xsmall" color="brandColor" />
  </div>
);

/**
 * Helper function to extract listing ID from a product URL
 * Handles URLs like: /pdp/package/{id}, /pdp/membership/{id}, /pdp/product/{id}, etc.
 */
const extractIdFromUrl = (
  url: string
): {
  type: "package" | "membership" | "product" | "giftcard" | null;
  id: string | null;
} => {
  const patterns = [
    { regex: /\/pdp\/package\/([^/?]+)/, type: "package" as const },
    { regex: /\/pdp\/membership\/([^/?]+)/, type: "membership" as const },
    { regex: /\/pdp\/product\/([^/?]+)/, type: "product" as const },
    { regex: /\/shop\/gift-cards\/([^/?]+)/, type: "giftcard" as const },
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern.regex);
    if (match && match[1]) {
      return { type: pattern.type, id: match[1] };
    }
  }

  return { type: null, id: null };
};

type ProductCardProps = {
  card: PriceCard | PromoCard;
  isV3: boolean;
  guestMode?: boolean;
};

/**
 * Individual product card with viewport detection for prefetching
 */
const ProductCard = ({ card, isV3, guestMode }: ProductCardProps) => {
  const [ref, isIntersecting] = useIntersectionObserver<HTMLLIElement>({
    rootMargin: "200px", // Start prefetching 200px before the card enters viewport
    threshold: 0,
    triggerOnce: true, // Only prefetch once
  });

  const {
    prefetchPackagePDP,
    prefetchMembershipPDP,
    prefetchProductPDP,
    prefetchGiftCards,
  } = usePrefetchPDP();

  useEffect(() => {
    if (!isIntersecting) return;

    const { type, id } = extractIdFromUrl(card.to);

    // Prefetch the appropriate PDP data based on the card type
    switch (type) {
      case "package":
        if (id) prefetchPackagePDP(id);
        break;
      case "membership":
        if (id) prefetchMembershipPDP(id);
        break;
      case "product":
        if (id) prefetchProductPDP(id);
        break;
      case "giftcard":
        // Gift cards share a common listing page
        prefetchGiftCards();
        break;
    }
  }, [
    isIntersecting,
    card.to,
    prefetchPackagePDP,
    prefetchMembershipPDP,
    prefetchProductPDP,
    prefetchGiftCards,
  ]);

  return (
    <li
      ref={ref}
      className={`w-56 shrink-0 ${
        isV3
          ? "overflow-hidden rounded-lg border border-neutral-300 bg-white shadow-md backdrop-blur-sm transition-shadow active:scale-[0.98]"
          : "overflow-clip rounded-lg shadow-md"
      }`}
    >
      <Link className="relative flex h-full flex-col" to={card.to}>
        {isV3 ? (
          // V3 Design
          <>
            <div className="relative">
              {"alert" in card && card.alert && (
                <div className="absolute left-3 top-3 z-10">
                  <BadgeTag color="alert">{card.alert}</BadgeTag>
                </div>
              )}
              {card.discount && (
                <div className="absolute left-2 top-2 z-10">
                  <DescriptiveTag
                    color={card.customColor ? "custom" : "primary"}
                    size="small"
                    customColor={card.customColor}
                  >
                    {card.discount}
                  </DescriptiveTag>
                </div>
              )}
              <img
                src={card.imageSrc}
                className="aspect-[4/3] w-full object-cover"
              />
              {/* Transparent gradient fade from image to card */}
              <div className="absolute bottom-0 h-8 w-full bg-gradient-to-b from-transparent to-white/80" />
            </div>
            <div className="flex flex-1 flex-col justify-center bg-white px-3 py-2">
              <h3 className="line-clamp-2 text-center font-serif text-h3 leading-tight tracking-tight text-primary">
                {card.title}
              </h3>
            </div>
            <div className="mt-auto flex w-full items-center bg-one px-3 py-2">
              {guestMode && <GuestFooter />}
              {!guestMode && "primary" in card && <PromoFooter {...card} />}
              {!guestMode && !("primary" in card) && <PriceFooter {...card} />}
            </div>
          </>
        ) : (
          // V2 Design
          <>
            {"alert" in card && card.alert && (
              <div className="absolute left-3 top-3">
                <BadgeTag color="alert">{card.alert}</BadgeTag>
              </div>
            )}
            <img
              src={card.imageSrc}
              className="aspect-[4/3] w-full rounded-t-lg object-cover"
            />
            <div className="flex flex-grow flex-col gap-4 rounded-b-lg bg-white p-3">
              <div className="flex gap-1">
                {card.discount && (
                  <DescriptiveTag
                    color={card.customColor ? "custom" : "primary"}
                    size="small"
                    customColor={card.customColor}
                  >
                    {card.discount}
                  </DescriptiveTag>
                )}
                {card.tag && (
                  <DescriptiveTag color="secondary" size="small">
                    {card.tag}
                  </DescriptiveTag>
                )}
              </div>
              <h3 className="line-clamp-2 flex-grow break-words font-serif text-h3">
                {card.title}
              </h3>
              {guestMode && <GuestFooter />}
              {!guestMode && "primary" in card && <PromoFooter {...card} />}
              {!guestMode && !("primary" in card) && <PriceFooter {...card} />}
            </div>
          </>
        )}
      </Link>
    </li>
  );
};

const ProductCarousel = (props: Props & BtnProps) => {
  const isV3 = true;

  return (
    <div className="flex w-full flex-col items-center gap-6 px-4 pb-8 pt-6">
      <div className="flex flex-col items-center gap-4 px-2 text-center">
        {props.svg && <Icon size="xsmall" svg={props.svg} color="brandColor" />}

        <div className="text-sub1 uppercase text-brand-color">
          {props.title}
        </div>

        {props.description && (
          <div className="text-body1 text-secondary">{props.description}</div>
        )}
      </div>

      <div className="w-full">
        <ul className="-m-4 flex gap-6 overflow-x-scroll p-4">
          {props.cards.map((card) => (
            <ProductCard
              key={card.to}
              card={card}
              isV3={isV3}
              guestMode={props.guestMode}
            />
          ))}
        </ul>
      </div>

      {props.primary && (
        <TextButton endIconSVG={Right} onClick={() => props.onButtonClick()}>
          {props.primary}
        </TextButton>
      )}
    </div>
  );
};

export default ProductCarousel;
